import moment from 'moment';

export function formatMediumDate(isoString) {
    if (!isoString) return '';
    return moment.utc(isoString).format('MMM D, YYYY');
}

export function getDefaultDateRangePresets() {
    const now = moment();
    const toRange = (start, end) => [start.toDate(), end.toDate()];
    const previousWeek = now.clone().subtract(1, 'week');
    const previousMonth = now.clone().subtract(1, 'month');

    return [
        {
            label: 'Last 7 Days',
            value: toRange(now.clone().subtract(6, 'days'), now.clone()),
            slot: 'preset-date-range-button',
        },
        {
            label: 'Last 30 Days',
            value: toRange(now.clone().subtract(29, 'days'), now.clone()),
            slot: 'preset-date-range-button',
        },
        {
            label: 'Last 90 Days',
            value: toRange(now.clone().subtract(89, 'days'), now.clone()),
            slot: 'preset-date-range-button',
        },
        {
            label: 'Prev. Week',
            value: toRange(
                previousWeek.clone().startOf('isoWeek'),
                previousWeek.clone().endOf('isoWeek')
            ),
            slot: 'preset-date-range-button',
        },
        {
            label: 'Prev. Month',
            value: toRange(
                previousMonth.clone().startOf('month'),
                previousMonth.clone().endOf('month')
            ),
            slot: 'preset-date-range-button',
        },
        {
            label: 'This Week',
            value: toRange(
                now.clone().startOf('isoWeek'),
                now.clone().endOf('isoWeek')
            ),
            slot: 'preset-date-range-button',
        },
        {
            label: 'This Month',
            value: toRange(
                now.clone().startOf('month'),
                now.clone().endOf('month')
            ),
            slot: 'preset-date-range-button',
        },
        {
            label: 'This Year',
            value: toRange(
                now.clone().startOf('year'),
                now.clone().endOf('year')
            ),
            slot: 'preset-date-range-button',
        },
    ];
}

/**
 * Convert a date-only value into a UTC day boundary ISO string.
 * @param {string|Date} dateValue
 * @param {'start'|'end'} boundary
 * @returns {string|null}
 */
function toUtcDayBoundaryIso(dateValue, boundary) {
    if (!dateValue) return null;

    const localMoment = moment(dateValue);
    if (!localMoment.isValid()) return null;

    const dateString = localMoment.format('YYYY-MM-DD');
    const utcMoment = moment.utc(dateString, 'YYYY-MM-DD', true);
    if (!utcMoment.isValid()) return null;

    return boundary === 'end'
        ? utcMoment.endOf('day').toISOString()
        : utcMoment.startOf('day').toISOString();
}

/**
 * Normalize a date range input into a start/end pair.
 * @param {string|Date|Array<string|Date>} value
 * @returns {{start: string|Date, end: string|Date}|null}
 */
export function normalizeDateRangeInput(value) {
    if (!value) return null;

    if (Array.isArray(value)) {
        const [start, end] = value;
        const rangeStart = start || end;
        const rangeEnd = end || start;
        return rangeStart ? { start: rangeStart, end: rangeEnd } : null;
    }

    return { start: value, end: value };
}

/**
 * Convert a date-only value into a UTC start-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayStartIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'start');
}

/**
 * Convert a date-only value into a UTC end-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayEndIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'end');
}
